import { useState, useEffect, ChangeEvent } from 'react';
import {
    Policy,
    State,
} from '@bluefalcon/clients-management-client-fetch';
import { carriersService } from '../../services';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    IconButton,
    Box,
    Spinner,
    Flex,
    Button,
    useDisclosure,
    Input,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from '../dropdown/dropdown.comp';
import { ConfirmModal } from '../../modals/confirm-modal.modal';
import { AddPolicyCountyModal } from '../../modals/add-policy-county.modal';
import { toast } from '../../utils/toast.utils';
import { ImportPolicyCountiesModal } from '../../modals/import-policy-counties.modal';

type PolicyCountyTableProps = {
    policy: Policy;
    states: State[];
};

type TableCounty = {
    stateId: string;
    stateName: string;
    countyId: number;
    countyName: string;
};

export const PolicyCountyTable = ({
    policy,
    states,
}: PolicyCountyTableProps) => {

    const mapCounties = (states?: State[]) => {
        return states?.flatMap((s) => {
            return s.counties.map((c) => ({
                stateId: s.id,
                stateName: s.name,
                countyId: c.id,
                countyName: c.value,
            }));
        }) ?? [];
    };

    const [counties, setCounties] = useState<TableCounty[]>(mapCounties(policy.states));
    const [isLoading, setLoading] = useState(false);
    const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const importPolicyCountiesControl = useDisclosure();
    const [selectedCounty, setSelectedCounty] = useState<TableCounty>();
    const [query, setQuery] = useState('');
    const [stateName, setStateName] = useState<string>();

    const onAddPolicyCounties = async (countyIds: number[]) => {
        try {
            setLoading(true);
            const res = await carriersService.updatePolicyById(policy.id, {
                id: policy.id,
                county: {
                    add: countyIds,
                },
            });
            toast('Added');
        } catch (error) { } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleDeleteRow = async () => {
        if (!selectedCounty) {
            return;
        }
        try {
            setLoading(true);
            await carriersService.updatePolicyById(policy.id, {
                id: policy.id,
                county: {
                    remove: [selectedCounty.countyId],
                },
            });
            toast('Removed');
        } catch (error) { } finally {
            setLoading(false);
            onClose();
        }
    };

    const openConfirmModal = (county: TableCounty) => {
        setSelectedCounty(county);
        onOpenConfirmModal();
    };

    const search = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setQuery(e.target.value);
    };

    const closeConfirmModal = () => {
        setSelectedCounty(undefined);
        onCloseConfirmModal();
    };

    const handleSetStateName = (name?: string) => {
        setStateName(policy.states?.find((s) => s.name === name)?.name);
    };

    useEffect(() => {
        const mapped = mapCounties(policy.states).filter((p) => {
            const hasQuery = query.trim() ? p.countyName.includes(query.trim()) : true;
            const hasState = stateName ? p.stateName === stateName : true;
            return hasQuery && hasState;
        });
        setCounties(mapped);
    }, [stateName, query]);

    return (
        <Box mr={3}>
            <Flex mb={2}>
                <Flex>
                    <Flex mr={3}>
                        <Input onChange={search} value={query} placeholder="search..." type={'search'} />
                        <Button type="submit" ml={2} colorScheme="primary">Search</Button>
                    </Flex>
                    <Dropdown
                        items={policy.states?.map((s) => s.name) ?? []}
                        onChange={handleSetStateName}
                        placeholder='State'
                        value={stateName}
                        name='state'
                    />
                </Flex>
                <Flex ml={'auto'} >
                    <Button
                        mr={2}
                        aria-label='Import Counties'
                        colorScheme="warn"
                        onClick={importPolicyCountiesControl.onOpen}
                    >
                        Import Counties
                    </Button>
                    <Button
                        aria-label='Add County'
                        colorScheme="teal"
                        onClick={onOpen}
                    >
                        Add County
                    </Button>
                </Flex>
            </Flex>
            {!isLoading ? (
                <TableContainer>
                    <Table variant="striped">
                        <Thead>
                            <Tr>
                                <Th>State</Th>
                                <Th>County</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {counties?.length ? (
                                counties.map((county, index) => (
                                    <Tr key={index}>
                                        <Td>{county.stateName}</Td>
                                        <Td>{county.countyName}</Td>
                                        <Td>
                                            <IconButton
                                                aria-label="Delete"
                                                icon={<FontAwesomeIcon icon={faTrash} />}
                                                onClick={() => openConfirmModal(county)}
                                            />
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={5} textAlign="center">
                                        No Counties.
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            ) : (
                <Spinner size="lg" />
            )}
            <ImportPolicyCountiesModal
                isOpen={importPolicyCountiesControl.isOpen}
                onClose={importPolicyCountiesControl.onClose}
                policy={policy}
            />
            <AddPolicyCountyModal
                policy={policy}
                closeModal={onClose}
                isOpen={isOpen}
                handleAddCounties={onAddPolicyCounties}
                states={states}
            />
            <ConfirmModal
                isOpen={isOpenConfirmModal}
                message='Are you sure you want to remove this county?'
                onCancel={closeConfirmModal}
                onClose={closeConfirmModal}
                onConfirm={handleDeleteRow}
            />
        </Box>
    );
};
